/** La paleta de Ctrl+K: buscar páginas por título y contenido, o ir a cualquier sitio. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api'
import { usePortal } from '../contexto'
import { hace } from '../textos'
import { IconoDePagina } from './Arbol'
import { useEspacio } from './contexto'
import type { Resultado } from './tipos'

interface Elemento {
  clave: string
  icono: React.ReactNode
  titulo: string
  detalle?: string
  grupo: string
  elegir: () => void
}

export default function Paleta() {
  const { servidor, puedo } = usePortal()
  const { rutaPaginas, cerrarPaleta, crear, abrirPapelera } = useEspacio()
  const navegar = useNavigate()
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState<Resultado[] | null>(null)
  const [recientes, setRecientes] = useState<Resultado[]>([])
  const [indice, setIndice] = useState(0)
  const lista = useRef<HTMLDivElement>(null)
  const base = `/${servidor.id}`

  useEffect(() => {
    api.get<Resultado[]>(`${rutaPaginas}/recientes`).then(setRecientes).catch(() => undefined)
  }, [rutaPaginas])

  // La búsqueda espera a que se deje de escribir: una petición por pulsación sería ruido.
  useEffect(() => {
    const texto = q.trim()
    if (!texto) {
      setResultados(null)
      return
    }
    const espera = setTimeout(() => {
      api
        .get<Resultado[]>(`${rutaPaginas}/buscar?q=${encodeURIComponent(texto)}`)
        .then(setResultados)
        .catch(() => setResultados([]))
    }, 180)
    return () => clearTimeout(espera)
  }, [q, rutaPaginas])

  const ir = (ruta: string) => () => {
    cerrarPaleta()
    navegar(ruta)
  }

  const elementos = useMemo<Elemento[]>(() => {
    const paginas = (resultados ?? recientes).map<Elemento>((r) => ({
      clave: `p${r.id}`,
      icono: <IconoDePagina icono={r.icon} />,
      titulo: r.title || 'Sin título',
      detalle: resultados ? r.fragmento : hace(r.updated_at),
      grupo: resultados ? 'Resultados' : 'Recientes',
      elegir: ir(`${base}/paginas/${r.id}`),
    }))
    const icono = (t: string, color: string) => <span className="pag-icono" style={{ color }}>{t}</span>
    const sitios: Elemento[] = [
      { clave: 'inicio', icono: icono('⌂', '#2383e2'), titulo: 'Inicio', grupo: 'Ir a', elegir: ir(base) },
      { clave: 'sol', icono: icono('✉', '#f97316'), titulo: 'Solicitudes', grupo: 'Ir a', elegir: ir(`${base}/solicitudes`) },
      { clave: 'nsol', icono: icono('＋', '#f97316'), titulo: 'Nueva solicitud', grupo: 'Acciones', elegir: ir(`${base}/solicitudes?nueva=1`) },
      { clave: 'men', icono: icono('@', '#ec4899'), titulo: 'Menciones', grupo: 'Ir a', elegir: ir(`${base}/menciones`) },
      { clave: 'tar', icono: icono('☑', '#16a34a'), titulo: 'Tareas', grupo: 'Ir a', elegir: ir(`${base}/tareas`) },
      { clave: 'pro', icono: icono('▤', '#8b5cf6'), titulo: 'Proyectos', grupo: 'Ir a', elegir: ir(`${base}/proyectos`) },
      { clave: 'equ', icono: icono('⚑', '#0ea5b7'), titulo: 'Equipo', grupo: 'Ir a', elegir: ir(`${base}/equipo`) },
      { clave: 'act', icono: icono('≡', '#eab308'), titulo: 'Actividad', grupo: 'Ir a', elegir: ir(`${base}/actividad`) },
    ]
    // Al buscar, también salen las secciones cuyo nombre coincide: escribir «tar» lleva a Tareas.
    if (resultados) {
      const t = q.trim().toLowerCase()
      return [...paginas, ...sitios.filter((s) => s.titulo.toLowerCase().includes(t))]
    }

    const acciones: Elemento[] = [
      ...(puedo('editar_paginas')
        ? [{
            clave: 'nueva', icono: <span className="pag-icono">＋</span>, titulo: 'Nueva página',
            grupo: 'Acciones', elegir: () => { cerrarPaleta(); void crear() },
          }]
        : []),
      ...(puedo('editar_paginas')
        ? [{
            clave: 'nuevabd', icono: <span className="pag-icono">▦</span>, titulo: 'Nueva base de datos',
            grupo: 'Acciones',
            elegir: () => { cerrarPaleta(); void crear({ kind: 'database', title: 'Base de datos' }) },
          }]
        : []),
      ...sitios,
      { clave: 'pap', icono: <span className="pag-icono">🗑</span>, titulo: 'Papelera', grupo: 'Ir a', elegir: () => { cerrarPaleta(); abrirPapelera(true) } },
    ]
    return [...paginas, ...acciones]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultados, recientes, base, q])

  useEffect(() => setIndice(0), [q, resultados])
  useEffect(() => {
    lista.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [indice])

  const tecla = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIndice((i) => Math.min(i + 1, elementos.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIndice((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      elementos[indice]?.elegir()
    } else if (e.key === 'Escape') {
      cerrarPaleta()
    }
  }

  let grupoAnterior = ''
  return (
    <div className="velo velo--paleta" onMouseDown={(e) => e.target === e.currentTarget && cerrarPaleta()}>
      <div className="paleta" role="dialog" aria-modal="true" aria-label="Buscar" onKeyDown={tecla}>
        <div className="paleta__entrada">
          <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true" fill="none"
               stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="8.5" cy="8.5" r="5.5" /><path d="m13 13 4 4" />
          </svg>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Busca una página o ve a cualquier sitio…"
            aria-label="Buscar"
          />
          <kbd>Esc</kbd>
        </div>
        <div className="paleta__lista" ref={lista} role="listbox">
          {elementos.length === 0 && (
            <div className="paleta__vacio">
              {resultados ? `Nada coincide con «${q.trim()}».` : 'Todavía no hay páginas.'}
            </div>
          )}
          {elementos.map((el, i) => {
            const cabecera = el.grupo !== grupoAnterior
            grupoAnterior = el.grupo
            return (
              <div key={el.clave}>
                {cabecera && <div className="paleta__grupo">{el.grupo}</div>}
                <div
                  role="option"
                  aria-selected={i === indice}
                  className={`paleta__fila${i === indice ? ' paleta__fila--activa' : ''}`}
                  onMouseMove={() => setIndice(i)}
                  onClick={el.elegir}
                >
                  {el.icono}
                  <span className="paleta__titulo">{el.titulo}</span>
                  {el.detalle && <span className="paleta__detalle">{el.detalle}</span>}
                </div>
              </div>
            )
          })}
        </div>
        <div className="paleta__pie">
          <span><kbd>↑</kbd><kbd>↓</kbd> moverse</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>Ctrl</kbd><kbd>K</kbd> cerrar</span>
        </div>
      </div>
    </div>
  )
}
